// react
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// radix
import * as RadixToast from '@radix-ui/react-toast';

// components
import { Icon } from '@/components/atoms/Icon/Icon';

type Variant = 'success' | 'error' | 'warning' | 'info' | 'plain';
type ToastSize = 'sm' | 'md';

export interface ToastProps {
  className?: string;
  title: string;
  description: string;
  variant?: Variant;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  duration?: number;
  size?: ToastSize;
  isOverrideBottom?: boolean;
}

/**
 * Toast provides a stylised notification.
 */
export const Toast = ({
  className,
  title,
  description,
  isOpen = false,
  setIsOpen,
  variant = 'plain',
  duration = 20000,
  size = 'md',
  isOverrideBottom = false,
  ...props
}: ToastProps) => {
  // methods
  const renderIcon = useCallback((variant: Variant, size: ToastSize) => {
    switch (variant) {
      case 'success':
        return <Icon icon='check' fontSize={size === 'sm' ? 10 : 14} />;
      case 'error':
        return <Icon icon='xmark' fontSize={size === 'sm' ? 10 : 14} />;
      case 'warning':
        return (
          <Icon icon='exclamation-circle' fontSize={size === 'sm' ? 10 : 14} />
        );
      case 'info':
        return <Icon icon='info-circle' fontSize={size === 'sm' ? 10 : 14} />;
    }
  }, []);

  // memos
  const computedDescription = useMemo(() => {
    // scan description for markdown-style links and plain URLs
    const desc: any[] = [];

    // Updated regex to match markdown-style links with spaces in `[]` and any URL in `()`
    const regex = /\[([^\]]+)\]\(([^)]+)\)|https?:\/\/[^\s]+/g;

    let match;
    let lastIndex = 0;

    // Iterate over all matches
    while ((match = regex.exec(description)) !== null) {
      const plainText = description.slice(lastIndex, match.index);
      if (plainText) {
        desc.push(plainText);
      }

      if (match[1] && match[2]) {
        // If match is markdown-style link
        desc.push(
          <a href={match[2]} target='_blank' rel='noreferrer' key={match.index}>
            {match[1]}
          </a>,
        );
      } else if (match[0]) {
        // If match is a plain URL
        desc.push(
          <a href={match[0]} target='_blank' rel='noreferrer' key={match.index}>
            {match[0]}
          </a>,
        );
      }

      lastIndex = regex.lastIndex;
    }

    // Push any remaining text after the last match
    if (lastIndex < description.length) {
      desc.push(description.slice(lastIndex));
    }

    return desc;
  }, [description]);

  const closeFontSize = useMemo(() => {
    switch (size) {
      case 'sm':
        return 16;
      default:
      case 'md':
        return 20;
    }
  }, [size]);

  return (
    <RadixToast.Provider swipeDirection='right' duration={duration}>
      <StyledToastRoot
        open={isOpen}
        onOpenChange={setIsOpen}
        $variant={variant}
        $size={size}
      >
        <StyledToastCloseContainer>
          <StyledToastClose aria-label='Close'>
            <span aria-hidden>
              <Icon icon='xmark' fontSize={closeFontSize} />
            </span>
          </StyledToastClose>
        </StyledToastCloseContainer>
        <StyledContainer>
          {variant !== 'plain' && (
            <StyledToastIcon $variant={variant} $size={size}>
              {renderIcon(variant, size)}
            </StyledToastIcon>
          )}
          <StyledContentContainer>
            <StyledToastTitle $size={size}>{title}</StyledToastTitle>
            <StyledToastDescription $size={size}>
              {computedDescription}
            </StyledToastDescription>
            {variant === 'error' && (
              <ErrorMessage>
                Please contact support at{' '}
                <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}>
                  {process.env.REACT_APP_SUPPORT_EMAIL}
                </a>{' '}
                if you continue to experience issues.
              </ErrorMessage>
            )}
          </StyledContentContainer>
        </StyledContainer>
        {/*
        // TODO: Implement actions
        <RadixToast.Action asChild altText="Goto schedule to undo">
          <button>Undo</button>
        </RadixToast.Action>
        */}
      </StyledToastRoot>
      <StyledToastViewport
        className={className}
        $size={size}
        $isOverrideBottom={isOverrideBottom}
      />
    </RadixToast.Provider>
  );
};

const StyledToastViewport = styled(RadixToast.Viewport)<{
  $size?: ToastSize;
  $isOverrideBottom?: boolean;
}>`
  // default styles
  display: flex;
  flex-direction: column;
  position: fixed;
  right: 0;
  display: flex;
  list-style: none;
  z-index: 2147483647;
  margin: 0;
  width: 400px;
  max-width: 100vw;
  outline: none;
  &:focus {
    outline: none;
  }

  ${(props) => {
    switch (props.$size) {
      case 'sm':
        return css`
          padding: ${props.theme.spacing[8]};
        `;
      default:
      case 'md':
        return css`
          padding: ${props.theme.spacing[24]};
        `;
    }
  }};

  bottom: 0;
  ${(props) =>
    props.theme.breakpoint.sm(css`
      bottom: 0;
    `)}
  ${(props) =>
    !props.$isOverrideBottom &&
    css`
      bottom: ${props.theme.spacing[64]};
    `}
`;

interface StyledToastRootProps {
  $variant: Variant;
  $size?: ToastSize;
}

const StyledToastRoot = styled(RadixToast.Root)<StyledToastRootProps>`
  // default styles
  position: relative;
  border-radius: 8px;
  border-width: 1px;
  border-style: solid;
  box-shadow: ${(props) => props.theme.elevation[4]};
  z-index: 2147483647;

  ${(props) => {
    switch (props.$size) {
      case 'sm':
        return css`
          --viewport-padding: ${(props) => props.theme.spacing[12]};
          padding: var(--viewport-padding);
        `;
      default:
      case 'md':
        return css`
          --viewport-padding: ${(props) => props.theme.spacing[20]};
          padding: var(--viewport-padding);
        `;
    }
  }};

  ${(props) => {
    switch (props.$variant) {
      case 'plain':
        return css`
          background: ${props.theme.color.neutral[10]};
          color: ${props.theme.color.neutral[90]};
          border-color: ${props.theme.color.neutral[50]};
        `;
      case 'success':
        return css`
          background: ${props.theme.color.success[20]};
          color: ${props.theme.color.success[60]};
          border-color: ${props.theme.color.success[30]};
        `;
      case 'error':
        return css`
          background: ${props.theme.color.danger[20]};
          color: ${props.theme.color.danger[60]};
          border-color: ${props.theme.color.danger[30]};
        `;
      case 'warning':
        return css`
          background: ${props.theme.color.warning[20]};
          color: ${props.theme.color.warning[60]};
          border-color: ${props.theme.color.warning[30]};
        `;
      case 'info':
        return css`
          background: ${props.theme.color.info[10]};
          color: ${props.theme.color.info[50]};
          border-color: ${props.theme.color.info[30]};
        `;
    }
  }}

  &[data-state='open'] {
    animation: slideIn 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state='closed'] {
    animation: hide 100ms ease-in;
  }
  &[data-swipe='move'] {
    transform: translateX(var(--radix-toast-swipe-move-x));
  }
  &[data-swipe='cancel'] {
    transform: translateX(0);
    transition: transform 200ms ease-out;
  }
  &[data-swipe='end'] {
    animation: swipeOut 100ms ease-out;
  }
  @keyframes hide {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes slideIn {
    from {
      transform: translateX(calc(100% + var(--viewport-padding)));
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes swipeOut {
    from {
      transform: translateX(var(--radix-toast-swipe-end-x));
    }
    to {
      transform: translateX(calc(100% + var(--viewport-padding)));
    }
  }
`;

const StyledToastTitle = styled(RadixToast.Title)<{ $size: ToastSize }>`
  grid-area: title;
  ${(props) => {
    switch (props.$size) {
      case 'sm':
        return css`
          font: ${(props) => props.theme.typography.body.md.bold};
        `;
      default:
      case 'md':
        return css`
          font: ${(props) => props.theme.typography.body.lg.bold};
        `;
    }
  }}
`;

const StyledToastDescription = styled(RadixToast.Description)<{
  $size: ToastSize;
}>`
  grid-area: description;
  ${(props) => {
    switch (props.$size) {
      case 'sm':
        return css`
          font: ${(props) => props.theme.typography.body.xs.regular};
        `;
      default:
      case 'md':
        return css`
          font: ${(props) => props.theme.typography.body.md.regular};
        `;
    }
  }}
`;

interface StyledToastIconProps {
  $variant: Variant;
  $size: ToastSize;
}

const StyledToastIcon = styled.div<StyledToastIconProps>`
  // default
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${(props) => props.theme.spacing[2]};
  margin-top: ${(props) => props.theme.spacing[4]};
  color: ${(props) => props.theme.color.neutral[10]};
  border-radius: 9999px;
  ${(props) => {
    switch (props.$size) {
      case 'sm':
        return css`
          min-width: 14px;
          min-height: 14px;
          max-width: 14px;
          max-height: 14px;
        `;
      default:
      case 'md':
        return css`
          min-width: 20px;
          min-height: 20px;
          max-width: 20px;
          max-height: 20px;
        `;
    }
  }};
  ${(props) => {
    switch (props.$variant) {
      case 'success':
        return css`
          background: ${props.theme.color.success[40]};
        `;
      case 'error':
        return css`
          background: ${props.theme.color.danger[40]};
        `;
      case 'warning':
        return css`
          background: ${props.theme.color.warning[40]};
          overflow: hidden;
          & > svg {
            min-width: 28px;
            min-height: 28px;
            transform: rotate(180deg);
          }
        `;
      case 'info':
        return css`
          background: ${props.theme.color.info[40]};
          // TODO: remove this once we have custom icons
          overflow: hidden;
          & > svg {
            min-width: 28px;
            min-height: 28px;
          }
        `;
    }
  }}
`;

const StyledContainer = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[8]};
`;

const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledToastCloseContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledToastClose = styled(RadixToast.Close)`
  border: none;
  background: none;
  cursor: pointer;
  padding: ${(props) => props.theme.spacing[12]};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const ErrorMessage = styled.div`
  font: ${(props) => props.theme.typography.body.xxs.regular};
  font-size: 8px;
  color: ${(props) => props.theme.color.danger[60]};
  padding-top: ${(props) => props.theme.spacing[4]};
`;

Toast.componentName = 'Toast';

export default Toast;
